import { motion } from "framer-motion";
import { EASE, Overline, Reveal } from "./Reveal";
import { IMAGES, WHATSAPP_URL, trackClick } from "../../lib/site";

const CHAPTERS = [
  {
    num: "01",
    title: "O sofrimento não surge no vazio",
    text: "Muitas vezes, o sofrimento parece estar apenas em você. Mas a exaustão emocional, os conflitos que se repetem ou os desafios de conviver com uma doença crônica não surgem de forma isolada. Eles são influenciados pela forma como nos relacionamos, pelos papéis que assumimos ao longo da vida, pelas experiências familiares e pelos padrões que aprendemos sem perceber.",
  },
  {
    num: "02",
    title: "As relações atravessam o que sentimos",
    text: "Na Terapia Sistêmica Familiar, compreendemos que o sofrimento emocional raramente afeta apenas uma pessoa. Seja diante da exaustão, dos conflitos familiares ou do impacto de uma doença crônica, as relações são atravessadas por mudanças, expectativas e formas de comunicação que podem intensificar — ou aliviar — esse sofrimento.",
  },
  {
    num: "03",
    title: "Além do alívio dos sintomas",
    text: "Por isso, o trabalho terapêutico vai além do alívio dos sintomas. Buscamos compreender o contexto em que sua história acontece, identificar padrões que hoje já não fazem sentido e construir maneiras mais saudáveis de lidar consigo, com as pessoas ao seu redor e com os desafios da sua realidade.",
  },
];

export const Manifesto = () => (
  <section
    id="abordagem"
    data-testid="manifesto-section"
    className="relative overflow-hidden py-24 lg:py-32"
  >
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <div className="grid grid-cols-1 gap-16 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-28">
            <Reveal>
              <Overline>Como a terapia funciona na prática</Overline>
              <h2 className="mt-6 font-serif text-3xl font-normal leading-tight tracking-tight text-bark sm:text-4xl lg:text-5xl">
                O que você vive tem uma{" "}
                <span className="italic text-brand">história</span>. Vamos
                compreendê-la juntos.
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, margin: "-70px" }}
                transition={{ duration: 1.1, delay: 0.2, ease: EASE }}
                className="mt-10 overflow-hidden rounded-[2rem] shadow-[0_24px_48px_rgba(45,51,47,0.12)]"
              >
                <motion.img
                  initial={{ scale: 1.12 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 1.6, ease: EASE }}
                  src={IMAGES.manifesto}
                  alt="Vanessa Calsavara em um atendimento online, em seu consultório"
                  className="aspect-[4/5] w-full object-cover object-[center_15%]"
                  data-testid="manifesto-image"
                />
              </motion.div>
            </Reveal>
          </div>
        </div>

        <div className="lg:col-span-7 lg:pl-8">
          {CHAPTERS.map((chapter, i) => (
            <Reveal key={chapter.num} delay={i * 0.08}>
              <div
                data-testid={`manifesto-chapter-${chapter.num}`}
                className="border-b border-bark/10 py-10 first:pt-0 lg:py-12"
              >
                <span className="font-serif text-lg italic text-clay">
                  Capítulo {chapter.num}
                </span>
                <h3 className="mt-3 font-serif text-2xl font-medium leading-snug text-bark sm:text-3xl">
                  {chapter.title}
                </h3>
                <p className="mt-5 max-w-2xl text-base leading-relaxed text-moss sm:text-lg">
                  {chapter.text}
                </p>
              </div>
            </Reveal>
          ))}

          <Reveal delay={0.1}>
            <blockquote
              data-testid="manifesto-highlight"
              className="mt-12 rounded-[2rem] bg-brand px-8 py-10 lg:px-12"
            >
              <p className="font-serif text-2xl font-medium leading-snug text-sand sm:text-3xl">
                “Você não precisa trazer sua família para a terapia.”
              </p>
              <p className="mt-5 text-base leading-relaxed text-sand/80">
                Mesmo no atendimento individual, essa perspectiva amplia a
                compreensão do que você vive — e favorece mudanças mais
                consistentes e duradouras.
              </p>
              <a
                data-testid="manifesto-cta"
                href={WHATSAPP_URL}
                onClick={() => trackClick("manifesto")}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 inline-flex items-center gap-3 rounded-full bg-sand px-7 py-3.5 text-sm font-semibold text-brand transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-white"
              >
                Entenda como essa abordagem pode ajudar você
              </a>
            </blockquote>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);
